/**
 * DurableObjectService — vpage-fe (Angular 21)
 *
 * Port từ `vpage-fe1/src/app/services/do-room.service.ts` sang Angular 21:
 * - `inject()` thay vì constructor injection.
 * - `isPlatformBrowser` guard để SSR-safe (không khởi `WebSocket` / `window` ở server).
 * - Lưu sạch listener `window`/`document` qua bound handler.
 * - `providedIn: 'root'`, standalone-ready.
 *
 * Quản lý WebSocket connections tới Cloudflare Durable Objects (DO).
 *
 * ## Kiến trúc Room
 * - `user-{userId}` → event riêng cho user (notification, reminder, force-logout…)
 * - `page-{pageId}` → event theo page (typing, conversation changes…)
 * - `business-{businessId}` → event theo doanh nghiệp (settings, member, tag, quick-message…)
 *
 * ## Sử dụng
 * ```ts
 * const doService = inject(DurableObjectService);
 *
 * // Sau khi đăng nhập
 * doService.joinUserRoom(userId);
 * doService.joinPageRooms(pageIds, userId);
 * doService.joinBusinessRooms(businessIds, userId);
 *
 * // Gửi typing
 * doService.emitTyping(pageId, { conversationId, userName, userId });
 *
 * // Lắng nghe
 * doService.onPageEvent(PAGE_EVENT_TYPE.CONVERSATION_TYPING).subscribe(...);
 * doService.onTyping().subscribe(...);
 * ```
 *
 * ## Chạy song song với Socket.IO
 * Service này chạy độc lập, KHÔNG thay thế socket.io ngay. Trong giai đoạn migration,
 * `environment.durable_object.ws_url` có thể rỗng — service tự tắt (`isConfigured()`
 * trả về `false`) và mọi `join*` đều no-op.
 *
 * @see [ADR-0002](../../../../../../../agent-vpage/tracking/decision-log.md)
 */
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {merge, Observable, Subject} from 'rxjs';
import {filter, map} from 'rxjs/operators';

import {environment} from '@environments/environment';
import {
  BUSINESS_EVENT_TYPE,
  DO_EVENT,
  DO_ROOM_TYPE,
  DoIncomingEvent,
  DoMessage,
  DoSubEventPayload,
  MOBILE_TRANSFER_TYPE,
  PAGE_EVENT_TYPE,
  StopTypingPayload,
  TypingPayload,
  USER_EVENT_TYPE,
  UserMemberChangedEventPayload,
} from '@shared/models/realtime/do-event.model';

// ─── Internal types ───────────────────────────────────────────────────────────

interface RoomConnection {
  roomId: string;
  roomType: DO_ROOM_TYPE;
  userId?: string;
  ws: WebSocket | null;
  status: 'idle' | 'connecting' | 'connected' | 'disconnected';
  reconnectDelay: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  destroyed: boolean;
}

/** Tập room phải tồn tại cho phiên auth hiện tại. */
export interface DoAuthRoomScope {
  userId?: string | number | null;
  pageIds?: Array<string | number> | null;
  businessIds?: Array<string | number> | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Bắt đầu reconnect sau 1s, tăng dần theo exponential backoff khi tiếp tục lỗi. */
const INITIAL_RECONNECT_DELAY = 1_000;
/** Giới hạn max delay để không phải chờ quá lâu khi mất kết nối. */
const MAX_RECONNECT_DELAY = 10_000;

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({providedIn: 'root'})
export class DurableObjectService implements OnDestroy {
  /** Stream tập trung tất cả events từ mọi room. */
  private readonly events$ = new Subject<DoIncomingEvent>();

  /** Map<roomId, RoomConnection>. */
  private readonly connections = new Map<string, RoomConnection>();

  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  /** Base WebSocket URL lấy từ environment; rỗng → service tự tắt. */
  private readonly wsBaseUrl: string =
    (environment as { durable_object?: { ws_url?: string } }).durable_object?.ws_url ?? '';

  /** Bound handlers để removeEventListener đúng cách. */
  private readonly onOnline = (): void => this.reconnectAllDisconnected();
  private readonly onVisibilityChange = (): void => {
    if (this.document.visibilityState === 'visible') {
      this.reconnectAllDisconnected();
    }
  };

  constructor() {
    if (!this.isBrowser) return;
    // Reconnect ngay khi mạng trở lại (mobile mất sóng, đổi WiFi↔4G, v.v.)
    window.addEventListener('online', this.onOnline);
    // Reconnect khi user quay lại tab (tab bị ẩn → DO có thể đã hibernate)
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  // ─── Public: join / leave rooms ──────────────────────────────────────────

  /** Join user room sau khi đăng nhập. */
  joinUserRoom(userId: string | number): void {
    if (!userId) return;
    const roomId = this.buildRoomId(DO_ROOM_TYPE.USER, userId);
    this.ensureConnected(roomId, DO_ROOM_TYPE.USER, String(userId));
  }

  /** Join tất cả page rooms theo danh sách `pageIds`. */
  joinPageRooms(pageIds: Array<string | number>, userId?: string | number): void {
    if (!pageIds?.length) return;
    for (const pageId of pageIds) {
      const roomId = this.buildRoomId(DO_ROOM_TYPE.PAGE, pageId);
      this.ensureConnected(roomId, DO_ROOM_TYPE.PAGE, userId ? String(userId) : undefined);
    }
  }

  /** Join tất cả business rooms theo danh sách `businessIds`. */
  joinBusinessRooms(businessIds: Array<string | number>, userId?: string | number): void {
    if (!businessIds?.length) return;
    for (const bizId of businessIds) {
      const roomId = this.buildRoomId(DO_ROOM_TYPE.BUSINESS, bizId);
      this.ensureConnected(roomId, DO_ROOM_TYPE.BUSINESS, userId ? String(userId) : undefined);
    }
  }

  /** Rời và đóng kết nối tới một room (roomId đầy đủ, vd `"page-abc123"`). */
  leaveRoom(roomId: string): void {
    const conn = this.connections.get(roomId);
    if (!conn) return;

    conn.destroyed = true;
    this.clearReconnectTimer(conn);
    conn.ws?.close(1000, 'leave room');
    this.connections.delete(roomId);
  }

  /** Rời tất cả page rooms theo danh sách. */
  leavePageRooms(pageIds: Array<string | number>): void {
    if (!pageIds?.length) return;
    for (const id of pageIds) {
      this.leaveRoom(this.buildRoomId(DO_ROOM_TYPE.PAGE, id));
    }
  }

  /** Rời tất cả business rooms theo danh sách. */
  leaveBusinessRooms(businessIds: Array<string | number>): void {
    if (!businessIds?.length) return;
    for (const id of businessIds) {
      this.leaveRoom(this.buildRoomId(DO_ROOM_TYPE.BUSINESS, id));
    }
  }

  /** Rời user room cụ thể. */
  leaveUserRoom(userId: string | number): void {
    if (!userId) return;
    this.leaveRoom(this.buildRoomId(DO_ROOM_TYPE.USER, userId));
  }

  /** Rời tất cả room hiện tại (dùng khi logout). */
  leaveAllRooms(): void {
    for (const roomId of Array.from(this.connections.keys())) {
      this.leaveRoom(roomId);
    }
  }

  /**
   * Đồng bộ room realtime với quyền hiện tại của người dùng.
   *
   * Không dùng join-only ở đây: sau khi cấp quyền lại, các page/business bị bỏ
   * phải đóng WebSocket cũ ngay để không tiếp tục đẩy hội thoại không còn quyền.
   */
  syncAuthRooms(scope: DoAuthRoomScope): void {
    if (!this.isConfigured()) return;

    const userId = scope.userId == null ? '' : String(scope.userId);
    const desired = new Map<string, {roomType: DO_ROOM_TYPE; userId?: string}>();

    if (userId) {
      desired.set(this.buildRoomId(DO_ROOM_TYPE.USER, userId), {
        roomType: DO_ROOM_TYPE.USER,
        userId,
      });
    }

    for (const pageId of this.normalizeRoomIds(scope.pageIds)) {
      desired.set(this.buildRoomId(DO_ROOM_TYPE.PAGE, pageId), {
        roomType: DO_ROOM_TYPE.PAGE,
        userId: userId || undefined,
      });
    }

    for (const businessId of this.normalizeRoomIds(scope.businessIds)) {
      desired.set(this.buildRoomId(DO_ROOM_TYPE.BUSINESS, businessId), {
        roomType: DO_ROOM_TYPE.BUSINESS,
        userId: userId || undefined,
      });
    }

    // Đóng stale room và reconnect room có user context thay đổi.
    for (const [roomId, connection] of Array.from(this.connections.entries())) {
      const target = desired.get(roomId);
      if (!target || connection.userId !== target.userId) {
        this.leaveRoom(roomId);
      }
    }

    for (const [roomId, target] of desired) {
      this.ensureConnected(roomId, target.roomType, target.userId);
    }
  }

  // ─── Public: send ─────────────────────────────────────────────────────────

  /** Gửi message tới room nếu WebSocket đang connected. */
  sendToRoom<T = unknown>(roomId: string, message: DoMessage<T>): void {
    const conn = this.connections.get(roomId);
    if (conn?.status === 'connected' && conn.ws?.readyState === WebSocket.OPEN) {
      try {
        conn.ws.send(JSON.stringify(message));
      } catch (err) {
        console.warn(`[DO] sendToRoom error (room=${roomId}):`, err);
      }
    }
  }

  /** Gửi typing event tới page room. */
  emitTyping(pageId: string, payload: Omit<TypingPayload, 'type'>): void {
    this.sendPageEvent(pageId, {
      type: PAGE_EVENT_TYPE.CONVERSATION_TYPING,
      ...payload,
    } as TypingPayload);
  }

  /** Gửi stop-typing event tới page room. */
  emitStopTyping(pageId: string, payload: Omit<StopTypingPayload, 'type'>): void {
    this.sendPageEvent(pageId, {
      type: PAGE_EVENT_TYPE.CONVERSATION_STOP_TYPING,
      ...payload,
    } as StopTypingPayload);
  }

  /** Gửi bất kỳ sub-event nào tới page room. */
  sendPageEvent<T extends DoSubEventPayload>(pageId: string, payload: T): void {
    const roomId = this.buildRoomId(DO_ROOM_TYPE.PAGE, pageId);
    this.sendToRoom(roomId, {event: DO_EVENT.PAGE_EVENT, data: payload});
  }

  /** Gửi sub-event tới business room. */
  sendBusinessEvent<T extends DoSubEventPayload>(businessId: string, payload: T): void {
    const roomId = this.buildRoomId(DO_ROOM_TYPE.BUSINESS, businessId);
    this.sendToRoom(roomId, {event: DO_EVENT.BUSINESS_EVENT, data: payload});
  }

  /** Gửi sub-event tới user room. */
  sendUserEvent<T extends DoSubEventPayload>(userId: string, payload: T): void {
    const roomId = this.buildRoomId(DO_ROOM_TYPE.USER, userId);
    this.sendToRoom(roomId, {event: DO_EVENT.USER_EVENT, data: payload});
  }

  // ─── Public: observe ─────────────────────────────────────────────────────

  /** Observable phát ra tất cả events từ mọi room. */
  onAllEvents(): Observable<DoIncomingEvent> {
    return this.events$.asObservable();
  }

  /** Observable phát ra events theo channel (DO_EVENT). */
  onEvent<T = unknown>(eventName: DO_EVENT | string): Observable<DoIncomingEvent<T>> {
    return this.events$.pipe(
      filter(e => e.event === eventName),
      map(e => e as DoIncomingEvent<T>),
    );
  }

  /** Lắng nghe sub-event cụ thể trong channel `page:event`. */
  onPageEvent<T extends DoSubEventPayload = DoSubEventPayload>(
    type: PAGE_EVENT_TYPE,
  ): Observable<DoIncomingEvent<T>> {
    return this.onEvent<T>(DO_EVENT.PAGE_EVENT).pipe(
      filter(e => (e.data as DoSubEventPayload)?.type === type),
    );
  }

  /** Lắng nghe sub-event cụ thể trong channel `business:event`. */
  onBusinessEvent<T extends DoSubEventPayload = DoSubEventPayload>(
    type: BUSINESS_EVENT_TYPE,
  ): Observable<DoIncomingEvent<T>> {
    return this.onEvent<T>(DO_EVENT.BUSINESS_EVENT).pipe(
      filter(e => (e.data as DoSubEventPayload)?.type === type),
    );
  }

  /** Lắng nghe sub-event cụ thể trong channel `user:event`. */
  onUserEvent<T extends DoSubEventPayload = DoSubEventPayload>(
    type: USER_EVENT_TYPE,
  ): Observable<DoIncomingEvent<T>> {
    return this.onEvent<T>(DO_EVENT.USER_EVENT).pipe(
      filter(e => (e.data as DoSubEventPayload)?.type === type),
    );
  }

  /** Lắng nghe sub-event cụ thể trong channel `mobile:transfer`. */
  onMobileTransfer<T extends DoSubEventPayload = DoSubEventPayload>(
    type: MOBILE_TRANSFER_TYPE,
  ): Observable<DoIncomingEvent<T>> {
    return this.onEvent<T>(DO_EVENT.MOBILE_TRANSFER).pipe(
      filter(e => (e.data as DoSubEventPayload)?.type === type),
    );
  }

  // ── Shorthands cho event phổ biến ────────────────────────────────────────

  /** Shorthand: lắng nghe `conversation:typing`. */
  onTyping(): Observable<DoIncomingEvent<TypingPayload>> {
    return this.onPageEvent<TypingPayload>(PAGE_EVENT_TYPE.CONVERSATION_TYPING);
  }

  /** Shorthand: lắng nghe `conversation:stop-typing`. */
  onStopTyping(): Observable<DoIncomingEvent<StopTypingPayload>> {
    return this.onPageEvent<StopTypingPayload>(PAGE_EVENT_TYPE.CONVERSATION_STOP_TYPING);
  }

  /** Shorthand: gộp typing + stop-typing thành 1 stream. */
  onUserTracking(): Observable<DoIncomingEvent<TypingPayload | StopTypingPayload>> {
    return merge(this.onTyping(), this.onStopTyping());
  }

  /** Shorthand: lắng nghe `conversation:changes`. */
  onDataChanges(): Observable<DoIncomingEvent> {
    return this.onPageEvent(PAGE_EVENT_TYPE.CONVERSATION_CHANGES);
  }

  /**
   * Shorthand: lắng nghe sự kiện thay đổi nhân viên/quyền/mật khẩu (member:changed) từ user room.
   * Dùng để tự động đăng xuất khi user bị đổi mật khẩu, đổi quyền, khóa tài khoản hoặc xóa khỏi doanh nghiệp.
   */
  onMemberChanged(): Observable<DoIncomingEvent<UserMemberChangedEventPayload>> {
    return this.onUserEvent<UserMemberChangedEventPayload>(USER_EVENT_TYPE.MEMBER_CHANGED);
  }

  // ─── Public: helpers ─────────────────────────────────────────────────────

  /** Build roomId đầy đủ từ type và id. Vd: `(PAGE, 'abc')` → `"page-abc"`. */
  buildRoomId(type: DO_ROOM_TYPE, id: string | number): string {
    return `${type}-${id}`;
  }

  /** Lấy trạng thái kết nối của một room. */
  getRoomStatus(roomId: string): RoomConnection['status'] | 'unknown' {
    return this.connections.get(roomId)?.status ?? 'unknown';
  }

  /** Kiểm tra service đã có URL cấu hình hay chưa (dùng để feature-flag). */
  isConfigured(): boolean {
    return this.isBrowser && !!this.wsBaseUrl;
  }

  /**
   * Được gọi khi native app resume; kiểm tra cả status nội bộ và readyState
   * thật của WebSocket vì app suspend có thể làm socket stale mà chưa phát close.
   */
  reconnectAfterAppResume(): void {
    if (!this.isConfigured()) return;

    for (const conn of this.connections.values()) {
      if (conn.destroyed) continue;

      const socketClosed = !conn.ws
        || conn.ws.readyState === WebSocket.CLOSED
        || conn.ws.readyState === WebSocket.CLOSING;
      if (conn.status === 'disconnected' || socketClosed) {
        this.clearReconnectTimer(conn);
        conn.reconnectDelay = INITIAL_RECONNECT_DELAY;
        this.openWebSocket(conn, conn.userId);
      }
    }
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    this.leaveAllRooms();
    if (this.isBrowser) {
      window.removeEventListener('online', this.onOnline);
      this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  // ─── Private: connection management ──────────────────────────────────────

  /** Reconnect tất cả room đang ở trạng thái `disconnected`. */
  private reconnectAllDisconnected(): void {
    for (const conn of this.connections.values()) {
      if (conn.destroyed) continue;
      if (conn.status === 'disconnected') {
        this.clearReconnectTimer(conn);
        conn.reconnectDelay = INITIAL_RECONNECT_DELAY;
        this.openWebSocket(conn, conn.userId);
      }
    }
  }

  /** Đảm bảo room đang connected; nếu chưa thì khởi tạo kết nối. */
  private ensureConnected(roomId: string, roomType: DO_ROOM_TYPE, userId?: string): void {
    if (!this.isConfigured()) return; // DO chưa cấu hình → skip

    const existing = this.connections.get(roomId);
    if (existing && (existing.status === 'connected' || existing.status === 'connecting')) {
      return;
    }

    const conn: RoomConnection = {
      roomId,
      roomType,
      userId,
      ws: null,
      status: 'idle',
      reconnectDelay: INITIAL_RECONNECT_DELAY,
      reconnectTimer: null,
      destroyed: false,
    };
    this.connections.set(roomId, conn);
    this.openWebSocket(conn, userId);
  }

  /** Mở WebSocket connection tới DO room. */
  private openWebSocket(conn: RoomConnection, userId?: string): void {
    if (conn.destroyed) return;
    if (!this.isBrowser) return;

    conn.status = 'connecting';

    let url = `${this.wsBaseUrl}/room/${conn.roomId}/ws`;
    // User room: userId đã embed trong roomId, không cần query.
    // Page/Business room: cần `?userId=` để Worker biết ai đang kết nối.
    if (userId && conn.roomType !== DO_ROOM_TYPE.USER) {
      url += `?userId=${encodeURIComponent(userId)}`;
    }

    try {
      const ws = new WebSocket(url);
      conn.ws = ws;
      ws.addEventListener('open', () => this.onWsOpen(conn));
      ws.addEventListener('message', ev => this.onWsMessage(conn, ev));
      ws.addEventListener('error', ev => this.onWsError(conn, ev));
      ws.addEventListener('close', ev => this.onWsClose(conn, ev));
    } catch (err) {
      console.error(`[DO] Cannot open WebSocket for room=${conn.roomId}:`, err);
      this.scheduleReconnect(conn);
    }
  }

  private onWsOpen(conn: RoomConnection): void {
    conn.status = 'connected';
    conn.reconnectDelay = INITIAL_RECONNECT_DELAY;
    this.events$.next({
      event: DO_EVENT.__CONNECTED,
      data: null,
      roomId: conn.roomId,
      roomType: conn.roomType,
    });
  }

  private onWsMessage(conn: RoomConnection, ev: MessageEvent): void {
    // Bỏ qua pong messages từ server.
    if (ev.data === 'pong' || ev.data === '__pong__') return;

    try {
      const msg: DoMessage = JSON.parse(ev.data);
      if (!msg?.event) return;
      this.events$.next({
        event: msg.event,
        data: msg.data,
        roomId: conn.roomId,
        roomType: conn.roomType,
      });
    } catch (err) {
      console.warn(`[DO] Failed to parse message from room=${conn.roomId}:`, ev.data, err);
    }
  }

  private onWsError(conn: RoomConnection, ev: Event): void {
    this.events$.next({
      event: DO_EVENT.__ERROR,
      data: ev,
      roomId: conn.roomId,
      roomType: conn.roomType,
    });
  }

  private onWsClose(conn: RoomConnection, ev: CloseEvent): void {
    conn.status = 'disconnected';
    this.events$.next({
      event: DO_EVENT.__DISCONNECTED,
      data: {code: ev.code, reason: ev.reason},
      roomId: conn.roomId,
      roomType: conn.roomType,
    });
    // Tự reconnect nếu không phải close chủ động (code 1000).
    if (!conn.destroyed && ev.code !== 1000) {
      this.scheduleReconnect(conn);
    }
  }

  // ─── Private: reconnect ───────────────────────────────────────────────────

  private scheduleReconnect(conn: RoomConnection): void {
    if (conn.destroyed) return;
    if (conn.status === 'connecting') return;

    this.clearReconnectTimer(conn);
    conn.reconnectTimer = setTimeout(() => {
      if (!conn.destroyed) {
        this.openWebSocket(conn, conn.userId);
      }
    }, conn.reconnectDelay);

    conn.reconnectDelay = Math.min(conn.reconnectDelay * 2, MAX_RECONNECT_DELAY);
  }

  private clearReconnectTimer(conn: RoomConnection): void {
    if (conn.reconnectTimer) {
      clearTimeout(conn.reconnectTimer);
      conn.reconnectTimer = null;
    }
  }

  private normalizeRoomIds(ids: Array<string | number> | null | undefined): string[] {
    return [...new Set((ids ?? [])
      .filter(id => id !== null && id !== undefined && String(id).trim() !== '')
      .map(id => String(id)))];
  }
}
